import { Col, Flex, Form, Input, message, Row, Select } from 'antd';
import { Store } from 'antd/es/form/interface';
import { useTranslation } from 'react-i18next';
import { useQueryClient } from '@tanstack/react-query';
import { PhoneNumberInput } from '@/components/PhoneNumberInput';
import { AddIcon } from '@/components/Icons';
import { ImageRequirementCard } from '@/components';
import { THEME_VARIABLE, LOCAL_AR } from '@/config';
import useLanguageStore from '@/stores/useLanguageStore';
import { useState } from 'react';
import AddRoleModal from './AddRoleModal';
import { useCreateTeamMember, useUpdateTeamMember } from '@/hooksApi';
import { Role } from '@/types';
import { attachCountryCodeToPhone, parsePhoneNumber } from '@/utils';
import { TeamMember } from '@/types';

interface Props {
  onClose: () => void;
  roleList?: Role[];
  teamMemberData?: TeamMember;
  teamMemberUID?: string;
}
const TeamMemberForm = ({ onClose, roleList, teamMemberData, teamMemberUID }: Props) => {
  const [roleUID, setRoleUID] = useState<string | undefined>(undefined);

  const { t } = useTranslation();
  const { language } = useLanguageStore();
  const [form] = Form.useForm();
  const queryClient = useQueryClient();

  // const { data: TeamMemberData, isLoading } = useFetchTeamMembersByUid(teamMemberUID);
  const { mutate: createTeamMembers } = useCreateTeamMember();
  const { mutate: updateTeamMembers } = useUpdateTeamMember();

  // useEffect(() => {
  //   if (teamMemberUID) {
  //     // if it's in editing mode => set form value
  //     // form.setFieldsValue({
  //     //   url: TeamMemberData?.object.url ?? '',
  //     //   triggers: TeamMemberData?.object.events?.map((event) => event.uid) ?? [],
  //     // });
  //   }
  //   return () => {
  //     form.resetFields();
  //     queryClient.invalidateQueries(['TeamMembers']);
  //   };
  // }, [teamMemberUID, TeamMemberData]);

  const onFinish = async (values: Store) => {
    const phone = attachCountryCodeToPhone(values.phone, values.countryCode);
    const selectedRoleUids = (values.role || []) as string[];

    if (teamMemberUID === '') {
      createTeamMembers(
        {
          first_name: values.first_name,
          last_name: values.last_name,
          email: values.email,
          phone,
          city_slug: values.city_slug,
          role_uids: selectedRoleUids,
        },
        {
          onSuccess: () => {
            onClose();
            queryClient.invalidateQueries(['teamMember-users']);
          },
          onError: () => { message.error(t('operation_failed_please_try_again')); },
        },
      );
    } else {
      updateTeamMembers(
        {
          uid: teamMemberData?.uid,
          first_name: values.first_name,
          last_name: values.last_name,
          email: values.email,
          phone,
          city_slug: values.city_slug,
          role_uids: selectedRoleUids,
        },
        {
          onSuccess: () => {
            onClose();
            queryClient.invalidateQueries(['teamMember-users']);
          },
          onError: () => { message.error(t('operation_failed_please_try_again')); },
        },
      );
    }
  };

  const onFinishFailed = (errorInfo: any) => {
    console.error('Form submission failed:', errorInfo);
    message.error(t('submission_failed'));
  };

  // return isLoading && !!teamMemberUID ? (
  //   <Spin size='large' className='flex justify-center items-center' />
  // ) :
  return (
    <>
      <Form
        form={form}
        name='TeamMember-form'
        className='py-2'
        onFinish={onFinish}
        onFinishFailed={onFinishFailed}
        initialValues={{
          ...teamMemberData,
          phone: parsePhoneNumber(teamMemberData?.phone)?.phone,
          countryCode: parsePhoneNumber(teamMemberData?.phone)?.countryCode,
          role: teamMemberData?.roles?.map((role) => role.uid) ?? [],
        }}
      >
        <Row gutter={[16, 0]} className='mt-4'>
          {/* <Col lg={24} md={24} xs={24} className='mb-4'>
            <ImageRequirementCard
              handleAvatarUpload={() => {}} // Use for logo upload
              isAvatarUploading={false}
              avatarUrl={undefined} // Provide the current avatar URL if available
            />
          </Col> */}
          <Col lg={12} md={12} xs={24}>
            <Form.Item
              name='first_name'
              rules={[{ required: true, message: t('this_field_is_required') }]}
            >
              <Input
                suffix={<span className='text-customRed text-base'>*</span>}
                variant='outlined'
                placeholder={t('first_name')}
                className='custom-input border custom-select-placeholder border-solid border-customGrayShade5 placeholder:text-GrayShade4 placeholder:font-[400] placeholder:text-[16px] placeholder:leading-[20.48px] h-[56px] rounded-lg w-full'
              />
            </Form.Item>
          </Col>
          <Col lg={12} md={12} xs={24}>
            <Form.Item
              name='last_name'
              rules={[{ required: true, message: t('this_field_is_required') }]}
            >
              <Input
                suffix={<span className='text-customRed text-base'>*</span>}
                variant='outlined'
                placeholder={t('last_name')}
                className='custom-input border custom-select-placeholder border-solid border-customGrayShade5 placeholder:text-GrayShade4 placeholder:font-[400] placeholder:text-[16px] placeholder:leading-[20.48px] h-[56px] rounded-lg w-full'
              />
            </Form.Item>
          </Col>

          <Col lg={12} md={24} xs={24} className='flex gap-x-2' style={{ direction: 'ltr' }}>
            <PhoneNumberInput
              form={form}
              countryCodeName={'countryCode'}
              phoneNumberName={'phone'}
              phoneNumberPlaceholder={t('primary_phone')}
              required
            />
          </Col>
          {/* <Col lg={12} md={12} xs={24}>
            <Form.Item
              name='email'
              rules={[
                {
                  type: 'email',
                  message: t('please_enter_a_valid_email'),
                  validateTrigger: 'onBlur',
                },
              ]}
            >
              <Input
                variant='outlined'
                placeholder={t('email')}
                className='custom-input border custom-select-placeholder border-solid border-customGrayShade5 placeholder:text-GrayShade4 placeholder:font-[400] placeholder:text-[16px] placeholder:leading-[20.48px] h-[56px] rounded-lg w-full'
              />
            </Form.Item>
          </Col> */}
          <Col lg={12} md={12} xs={24}>
            <Form.Item
              name='role'
              rules={[{ required: true, message: t('this_field_is_required') }]}
            >
              <Select
                mode='multiple'
                className='custom-multi-select-item custom-select-placeholder min-h-[56px] h-fit rounded-[10px]'
                variant='borderless'
                allowClear
                style={{ width: '100%' }}
                suffixIcon={<span className='text-customRed text-base'>*</span>}
                placeholder={t('role')}
                dropdownRender={(menu) => (
                  <>
                    {menu}
                    <div
                      onClick={() => {
                        setRoleUID('');
                      }}
                      className='border-customGrayShade3 border-solid border-0 border-t py-2 px-3 cursor-pointer hover:bg-gray-50'
                    >
                      <Flex gap={8}>
                        <AddIcon width={20} height={20} color={THEME_VARIABLE.customTunaPrimary} />
                        <span>{t('add_a_custom_role')}</span>
                      </Flex>
                    </div>
                  </>
                )}
              >
                {roleList?.map((role) => (
                  <Select.Option key={role.uid} value={role.uid}>
                    {language === LOCAL_AR && role.description ? role.description : role.name}
                  </Select.Option>
                ))}
              </Select>
            </Form.Item>
          </Col>
        </Row>

        <Flex className='mt-8' justify='end' gap={16}>
          <button
            type='button'
            onClick={onClose}
            className='flex items-center gap-1.5 h-9 px-4 rounded-lg border border-solid border-[rgb(232,230,227)] bg-white text-[rgb(57,57,60)] text-[13px] font-semibold cursor-pointer disabled:opacity-50 disabled:cursor-not-allowed hover:bg-[rgb(245,245,243)] transition-colors uppercase'
          >
            {t('cancel')}
          </button>
          <button
            type='submit'
            className='flex items-center gap-1.5 h-9 px-5 rounded-lg bg-[rgb(237,224,74)] text-[rgb(57,57,60)] text-[13px] font-semibold cursor-pointer disabled:opacity-50 disabled:cursor-not-allowed hover:bg-[rgb(230,217,60)] transition-colors uppercase'
          >
            {teamMemberData?.uid ? undefined : <AddIcon width={20} height={20} />}
            {teamMemberData?.uid ? t('update') : t('create')}
          </button>
        </Flex>
      </Form>
      <AddRoleModal roleUID={roleUID} onClose={() => setRoleUID(undefined)} />
    </>
  );
};

export default TeamMemberForm;
